import { Injectable } from '@angular/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select } from '@ngxs/store';
import {
  AgendaHttpService,
  EventSubHttpService,
  FittersHttpService,
} from '@sc/data-access-http-services';
import { Fitter } from '@sc/data-access-models';
import {
  JsonListKey,
  JsonObjectKey,
  StorageService,
} from '@sc/data-access-storage';
import { AuthService } from '@sc/feature-auth-okta';
import { dayjs } from '@sc/utils';
import { ConfigType } from 'dayjs';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, switchMapTo, take, tap } from 'rxjs/operators';
import { SetFittersAction } from '../stores';

export type CalendarExtremitySlotTimes = Record<
  string,
  {
    startSlotTime: string;
    endSlotTime: string;
  }
>;

@Injectable({ providedIn: 'root' })
export class FittersManagerService {
  @Select((state) => state.agendaResources.fitters)
  fitters$: Observable<Fitter[]>;

  constructor(
    private readonly eventSubHttpService: EventSubHttpService,
    private readonly authOktaService: AuthService,
    private readonly agendaHttpService: AgendaHttpService,
    private readonly fittersHttpService: FittersHttpService,
    private readonly storage: StorageService
  ) {}

  public getOrFetchCurrentFitters() {
    return this.setPlanningFitters([]);
  }

  public setPlanningFitters(fitterIds: string[], allowEmpty = false) {
    return this.fitters$.pipe(
      take(1),
      switchMap((currentFitters) => {
        return combineLatest([
          of(currentFitters),
          this.authOktaService.userIsFitterSuperAdmin$(),
        ]);
      }),
      switchMap(([currentFitters, isFitterAdmin]) => {
        let usedFitterIds: string[] = [];
        if (fitterIds?.length > 0 || allowEmpty) {
          usedFitterIds = fitterIds;
        } else if (currentFitters?.length > 0) {
          usedFitterIds = currentFitters.map((f) => f.id);
        } else {
          usedFitterIds = this.getSavedFitterIds();
        }

        const oldFitters =
          currentFitters?.filter(
            (currentF) =>
              usedFitterIds.findIndex((fId) => currentF.id === fId) === -1
          ) || [];
        const fittersToFetch = usedFitterIds.filter(
          (fId) =>
            !currentFitters ||
            currentFitters?.findIndex((f) => f.id === fId) === -1
        );
        const fittersToKeep = isFitterAdmin
          ? currentFitters?.filter(
              (f) => usedFitterIds.findIndex((fId) => f.id === fId) > -1
            ) || []
          : [];

        let resultObs = of<Fitter[]>([]);
        if (fittersToFetch?.length > 0 && isFitterAdmin) {
          // If admin, fetch the wanted fitters that are not already in store
          resultObs = resultObs.pipe(
            switchMapTo(
              this.agendaHttpService.getFittersByIds({
                ids: fittersToFetch,
              })
            )
          );
        } else if (!isFitterAdmin) {
          // If not admin, always override the result with managed fitters only
          resultObs = resultObs.pipe(
            switchMapTo(this.fittersHttpService.getFittersByManager())
          );
        }

        return resultObs.pipe(
          map((fetchedFitters) => [...fetchedFitters, ...fittersToKeep]),
          map((usedFitters) => {
            const usedFittersSorted = [...usedFitters];
            usedFittersSorted.sort(
              (f1, f2) =>
                usedFitterIds.indexOf(f1.id) - usedFitterIds.indexOf(f2.id)
            );

            return [usedFittersSorted, oldFitters] as [Fitter[], Fitter[]];
          })
        );
      }),
      tap(([fitters, oldFitters]) => {
        oldFitters.forEach((oldFitter) => {
          this.eventSubHttpService.unsubscribe(
            `#${oldFitter.brandId}_${oldFitter.id}`
          );
        });
        fitters.forEach((fitter) => {
          this.eventSubHttpService.checkSubscription(
            `${fitter.brandId}_${fitter.id}`
          );
        });
        this.saveFitterIds(fitters.map((f) => f.id));
      }),
      map(([fitters]) => {
        this.storeFitters(fitters);
        return fitters ?? [];
      })
    );
  }

  unsubscribeAll() {
    this.eventSubHttpService.unsubscribe('#');
  }

  subscribeToCurrentFitters() {
    this.setPlanningFitters(this.getSavedFitterIds(), false).subscribe();
  }

  getSavedFitterIds() {
    return this.storage.getItem<JsonListKey, string>(
      'agenda-fitters-displayed',
      localStorage,
      []
    );
  }

  saveFitterIds(fitterIds: string[]) {
    this.storage.setItem(
      'agenda-fitters-displayed',
      fitterIds,
      localStorage,
      []
    );
  }

  @Dispatch()
  storeFitters(fitters: Fitter[]): Observable<SetFittersAction> {
    return of(new SetFittersAction(fitters));
  }

  getFitterExtremitiesFromOpenings(
    fitter: Fitter,
    currentDay: ConfigType,
    hiddenDays: { [key: number]: boolean }
  ) {
    const currentDayMoment = dayjs(currentDay).utc(true);

    const openingsToTest = [
      ...(fitter.openingHours?.center || []),
      ...(fitter.openingHours?.outside || []),
    ].filter((opening) => !hiddenDays[opening.open.day]);

    if (!openingsToTest.length) {
      openingsToTest.push(
        ...(fitter.openingHours?.center || [
          {
            open: { day: currentDayMoment.isoWeekday(), time: '0700' },
            close: { day: currentDayMoment.isoWeekday(), time: '2000' },
          },
        ])
      );
    }

    const openingStartsToTest = openingsToTest.map((opening) =>
      currentDayMoment
        .clone()
        .hour(+opening.open.time.slice(0, 2))
        .minute(+opening.open.time.slice(2))
        .startOf('minute')
        .unix()
    );
    const openingEndsToTest = openingsToTest.map((opening) =>
      currentDayMoment
        .clone()
        .hour(+opening.close.time.slice(0, 2))
        .minute(+opening.close.time.slice(2))
        .startOf('minute')
        .unix()
    );

    if (!openingStartsToTest.length) {
      openingStartsToTest.push(
        currentDayMoment.clone().hour(7).minute(0).unix()
      );
    }
    if (!openingEndsToTest.length) {
      openingEndsToTest.push(
        currentDayMoment.clone().hour(20).minute(0).unix()
      );
    }

    const earliestOpening = Math.min(...openingStartsToTest);
    const latestOpening = Math.max(...openingEndsToTest);

    return {
      startSlotTime: dayjs.unix(earliestOpening).utc(),
      endSlotTime: dayjs.unix(latestOpening).utc(),
    };
  }

  getExtremityDefaultSlotTimes(
    currentDay: ConfigType,
    hiddenDays: { [key: number]: boolean }
  ) {
    const storedExtremities = this.storage.getItem<
      JsonObjectKey,
      CalendarExtremitySlotTimes
    >('calendar-extremity-slot-times', localStorage, {});

    const currentDayMoment = dayjs(currentDay).utc().startOf('day');

    return this.fitters$.pipe(
      filter((fitters) => !!fitters?.length),
      take(1),
      map((fitters) =>
        fitters.map((f) => {
          const storedFitterStartTime =
            storedExtremities?.[f.id]?.startSlotTime;
          const storedFitterEndTime = storedExtremities?.[f.id]?.endSlotTime;

          const fitterOpenings = this.getFitterExtremitiesFromOpenings(
            f,
            currentDay,
            hiddenDays
          );

          const formattedStart = dayjs(storedFitterStartTime, 'HH:mm:ss').utc(
            true
          );
          const formattedEnd = dayjs(storedFitterEndTime, 'HH:mm:ss').utc(true);

          const finalStartTime = formattedStart.isValid()
            ? currentDayMoment
                .clone()
                .hour(formattedStart.hour())
                .minute(formattedStart.minute())
                .startOf('minute')
            : fitterOpenings?.startSlotTime;

          const finalEndTime = formattedEnd.isValid()
            ? currentDayMoment
                .clone()
                .hour(formattedEnd.hour())
                .minute(formattedEnd.minute())
                .startOf('minute')
            : fitterOpenings?.endSlotTime;

          return {
            id: f.id,
            start: finalStartTime,
            end: finalEndTime,
          };
        })
      )
    );
  }
}
